import type { PositionBracketEntry } from '@flipster/apis/v2/snapshot';
import Num, { BigNumber } from '@flipster/utils/num';

import { toNonNegativeDecimal, toPositiveDecimal } from './number-guards';

/**
 * Bracket tier the calc consumes: a `PositionBracketEntry` (apis/v2/snapshot)
 * augmented with the cumulative `maintAmount` constant from prex-core's
 * `get_margin_rate_array` recurrence.
 */
export type BracketTierWithMaintAmount = PositionBracketEntry & {
  maintAmount: string;
};

interface ParsedBracketTier {
  notionalUpperLimit: BigNumber;
  maintMarginRate: BigNumber;
}

const isParsedBracketTier = (tier: ParsedBracketTier | null): tier is ParsedBracketTier => tier !== null;

/**
 * Mirrors `prex-core::risk::util::get_margin_rate_array` — pre-computes the
 * cumulative `maint_amount` per bracket tier so the bracket walker can solve
 * for liq price in closed form.
 */
export function computeMaintAmounts(tiers: PositionBracketEntry[]): BracketTierWithMaintAmount[] {
  const parsedTiers = tiers.map((tier) => {
    const notionalUpperLimit = toPositiveDecimal(tier.notionalPositionSizeUpperLimit);
    const maintMarginRate = toNonNegativeDecimal(tier.maintMarginRate);

    if (!notionalUpperLimit || !maintMarginRate) return null;

    return { notionalUpperLimit, maintMarginRate };
  });
  const validParsedTiers = parsedTiers.filter(isParsedBracketTier);

  if (validParsedTiers.length !== tiers.length) return [];

  const out: BracketTierWithMaintAmount[] = [];
  let cumulative: BigNumber = Num('0');
  let prevMaintMarginRate: BigNumber = Num('0');
  let prevNotionalUpperLimit: BigNumber = Num('0');

  for (let i = 0; i < tiers.length; i++) {
    const tier = tiers[i];
    const parsedTier = validParsedTiers[i];
    const maintMarginRate = parsedTier.maintMarginRate;

    cumulative = cumulative.plus(maintMarginRate.minus(prevMaintMarginRate).multipliedBy(prevNotionalUpperLimit));

    out.push({ ...tier, maintAmount: cumulative.toString() });

    prevMaintMarginRate = maintMarginRate;
    prevNotionalUpperLimit = parsedTier.notionalUpperLimit;
  }

  return out;
}
